import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import UploadIcon from '@mui/icons-material/Upload'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { uploadImageToGhl } from 'hook/services'
import AvatarPreview from './AvatarPreview'
import { BLUE, blueSwitchSx, NAME_MAX } from '../utils'

// The header renders it at 42px and the message list at 30px, so anything
// bigger is bytes the visitor downloads and never sees.
const MAX_AVATAR_BYTES = 2 * 1024 * 1024

/**
 * Avatar, name, tagline, status pill and launcher bubble — everything the
 * visitor reads before the assistant has said a word.
 */
function IdentityFields({ form, onField, onText }) {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0]

    // Reset the input straight away, so picking the same file twice after a
    // failed upload still fires a change event.
    e.target.value = ''
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setUploadError('That is not an image file.')
      return
    }

    if (file.size > MAX_AVATAR_BYTES) {
      setUploadError('Keep the avatar under 2 MB — it renders at 42px.')
      return
    }

    setUploading(true)
    setUploadError('')

    const payload = new FormData()
    payload.append('file', file, file.name)

    try {
      const response = await uploadImageToGhl(payload)

      // The handler answers 200 with success:false when GHL rejects the file,
      // so the body has to be checked as well as the transport.
      const url = response?.imageUrl || response?.data
      if (!url) throw new Error(response?.message || 'Upload failed. Try again.')

      onField('avatarUrl')(url)
    } catch (err) {
      setUploadError(err?.message || 'Upload failed. Try again.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm="auto">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <AvatarPreview src={form.avatarUrl} size={76} radius={12} iconSize={38} />

          <Button
            component="label"
            size="small"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />}
            sx={{ color: BLUE.main }}
          >
            {uploading ? 'Uploading…' : 'Avatar'}
            <input hidden type="file" accept="image/*" onChange={handleAvatar} />
          </Button>

          {form.avatarUrl ? (
            <Button size="small" color="error" onClick={() => onField('avatarUrl')('')}>
              Remove
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Robot icon
            </Typography>
          )}
        </Box>
      </Grid>

      <Grid item xs={12} sm>
        {uploadError ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>
            {uploadError}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Name"
              value={form.botName}
              onChange={onText('botName')}
              inputProps={{ maxLength: NAME_MAX }}
              helperText="Shown in bold at the top of the chat."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Tagline"
              value={form.tagline}
              onChange={onText('tagline')}
              helperText="The small grey line under the name. Blank hides it."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Status pill"
              value={form.statusText}
              onChange={onText('statusText')}
              disabled={!form.showStatus}
              helperText="The green dot beside the name, e.g. “Online”."
            />
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Switch
                  checked={form.showStatus}
                  onChange={(e) => onField('showStatus')(e.target.checked)}
                  sx={blueSwitchSx}
                />
              }
              label={<Typography variant="caption">Show the status pill</Typography>}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Launcher bubble"
              value={form.teaser}
              onChange={onText('teaser')}
              helperText="Pops out beside the closed chat button. Blank hides it."
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

IdentityFields.propTypes = {
  form: PropTypes.shape({
    botName: PropTypes.string,
    tagline: PropTypes.string,
    statusText: PropTypes.string,
    showStatus: PropTypes.bool,
    avatarUrl: PropTypes.string,
    teaser: PropTypes.string,
  }).isRequired,
  onField: PropTypes.func.isRequired,
  onText: PropTypes.func.isRequired,
}

export default IdentityFields
